// Exemplos prontos do simulador 2D (Meep). Cada exemplo é descrito em unidades físicas (µm)
// e convertido para o formato do canvas (pixels) ao criar a cópia na conta do usuário.
// Os resultados de referência permitem conferir se a plataforma reproduz a literatura.
import md5 from "js-md5";
import { defaultMonitor, defaultSource } from "@/store/module-simulator/lib/monitors";
import { wavelengthToFrequency } from "@/store/module-simulator/lib/units";

// "key" liga cada material ao material pronto do backend (backend/src/seeds/builtinMaterials.js)
const SILICON = { n: 3.46, color: "#455A64", key: "silicon" };
const ANALYTE = { n: 1.45, color: "#E3F2FD", key: "analyte-1.45" };

const PLACEHOLDER_IMAGE =
  "https://satemrj.com.br/site/wp-content/plugins/penci-soledad-amp/assets/images/no-thumb.jpg";

const DEFAULT_PLOT_OPTIONS = {
  generateAxis: 1,
  generateVideo: 0,
  generateDielectric: 1,
  plotAxis: "Ez",
  realtime: 0,
  labels: 0,
  field_parameters: { interpolation: "spline36", alpha: 1, cmap: "RdBu" },
  monitor_parameters: {
    alpha: 1, color: "g", facecolor: "none", edgecolor: "r", linewidth: 1,
    hatch: "o", label_color: "g", label_alpha: 0.3, offset: 20
  },
  source_parameters: {
    color: "r", alpha: 1, facecolor: "none", edgecolor: "r", linewidth: 1,
    hatch: "", label_color: "r", label_alpha: 0, offset: 20
  },
  plotViews: { eps_flag: 0, sources_flag: 0, monitor_flag: 0, boundaries_flag: 0 },
  boundary_parameters: { alpha: 1, facecolor: "g", edgecolor: "g", linewidth: 1, hatch: "" },
  eps_parameters: { alpha: 1, interpolation: "spline36", cmap: "binary" }
};

// ---------------------------------------------------------------------------
// Conversão µm -> canvas (mesma escala de lib/cellOptions.js: o eixo maior tem 400 px)
// ---------------------------------------------------------------------------
const canvasScale = (cell) => 400 / Math.max(cell.x, cell.y);

const toCanvas = (cell, x, y) => {
  const scale = canvasScale(cell);
  return [(x + cell.x / 2) * scale, (cell.y / 2 - y) * scale];
};

let tokenCounter = 0;
const newToken = () => md5.hex(`${Date.now()}-${tokenCounter++}-${Math.random()}`);

const baseGeometry = (name, epsilon, color, normalization, materialKey) => ({
  name,
  materialKey,
  token: newToken(),
  class: "geometry",
  fill: color,
  epsilon,
  materialId: 0,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
  normalization,
  geometricFill: {
    properties: { name: `Default ${name}`, width: 1, height: 1, image: "" },
    elements: []
  },
  geometricFillPattern: []
});

const circle = (cell, { name, x, y, r, n, color, normalization = "device", material }) => {
  const [cx, cy] = toCanvas(cell, x, y);
  const radius = r * canvasScale(cell);
  return {
    ...baseGeometry(name, n * n, color, normalization, material),
    shape: "sphere",
    x: cx, y: cy, radius, width: radius, height: radius
  };
};

const block = (cell, { name, x, y, width, height, n, color, normalization = "device", material }) => {
  // O Konva posiciona o retângulo pelo canto superior esquerdo
  const [left, top] = toCanvas(cell, x - width / 2, y + height / 2);
  const scale = canvasScale(cell);
  return {
    ...baseGeometry(name, n * n, color, normalization, material),
    shape: "block",
    x: left, y: top, width: width * scale, height: height * scale
  };
};

const source = (cell, index, { x, y, ...options }) => {
  const [cx, cy] = toCanvas(cell, x, y);
  const result = { ...defaultSource({ token: newToken(), x: cx, y: cy, index }), ...options };
  result.frequency = wavelengthToFrequency(result.waveLength);
  return result;
};

const monitor = (cell, index, { x, y, ...options }) => {
  const [cx, cy] = toCanvas(cell, x, y);
  return { ...defaultMonitor({ token: newToken(), x: cx, y: cy, index }), ...options };
};

// ---------------------------------------------------------------------------
// Sensores de cristal fotônico (Balur, Sonkusare e Sharan, IEEE Xplore 11525706),
// conforme a reprodução em Meep de github.com/electrosfi-org/meepIEEE2025Test:
// hastes de Si (n = 3,46, r = 0,19 µm) em analito n = 1,45, rede quadrada a = 1 µm 29 × 23,
// guia W1 (linha y = 0 removida) e um anel de hastes removidas em volta da origem.
// ---------------------------------------------------------------------------
const RING_REMOVED = {
  square: [
    ...[-3, -2, -1, 0, 1, 2, 3].flatMap(x => [[x, 3], [x, -3]]),
    ...[3, -3].flatMap(x => [[x, 1], [x, -1], [x, 2], [x, -2]])
  ],
  hexagonal: [
    ...[-2, -1, 0, 1, 2].flatMap(x => [[x, 3], [x, -3]]),
    ...[3, -3].flatMap(x => [[x, 2], [x, -2]]),
    ...[4, -4].flatMap(x => [[x, 1], [x, -1]])
  ]
};

const photonicCrystalSensor = (ring) => {
  const cell = { x: 29, y: 23 };
  const removed = new Set(RING_REMOVED[ring].map(([x, y]) => `${x},${y}`));
  const rods = [];
  let count = 0;
  for (let y = -11; y <= 11; y++) {
    if (y === 0) continue; // guia W1
    for (let x = -14; x <= 14; x++) {
      const ringRod = removed.has(`${x},${y}`);
      count += 1;
      rods.push(circle(cell, {
        name: `${ringRod ? "ring rod" : "rod"} ${count}`,
        x, y, r: 0.19, n: SILICON.n, color: SILICON.color, material: SILICON.key,
        // As hastes do anel só existem no cristal de referência (W1 sem cavidade), usado
        // para normalizar a transmissão; as demais existem nas duas rodadas
        normalization: ringRod ? "reference" : "both"
      }));
    }
  }
  const fcen = 0.3;
  return {
    resolution: 10,
    coordinates: { x: cell.x, y: cell.y, z: 0 },
    pml: 1,
    background: { n: ANALYTE.n, color: ANALYTE.color, title: "Analyte (n = 1.45)", key: ANALYTE.key },
    until: 200,
    geometries: rods,
    sources: [source(cell, 1, {
      x: -12, y: 0, sourceType: "gaussian", component: "Ez",
      waveLength: Number((1 / fcen).toFixed(6)), fwidth: 0.1, waveSize: [0, 1]
    })],
    flux: [
      monitor(cell, 1, {
        x: 0, y: 0, kind: "resonance", name: "Cavity center (Harminv)", fcen, df: 0.1
      }),
      monitor(cell, 2, {
        x: 13, y: 0, kind: "transmission", name: "Bus waveguide output",
        orientation: "vertical", length: 2, fcen, df: 0.1, nfreq: 201
      })
    ]
  };
};

// ---------------------------------------------------------------------------
// Anel ressonador (tutorial "Ring Resonator" da documentação do Meep)
// ---------------------------------------------------------------------------
const ringResonator = () => {
  const cell = { x: 16, y: 16 };
  return {
    resolution: 10,
    coordinates: { x: cell.x, y: cell.y, z: 0 },
    pml: 2,
    until: 200,
    geometries: [
      circle(cell, { name: "ring (n = 3.4)", x: 0, y: 0, r: 2, n: 3.4, color: "#546E7A", material: "dielectric-3.4" }),
      // Desenhada depois, a região interna (ar) vence a de dentro do anel
      circle(cell, { name: "ring hole (air)", x: 0, y: 0, r: 1, n: 1, color: "#FFFFFF", material: "vacuum" })
    ],
    sources: [source(cell, 1, {
      x: 1.1, y: 0, sourceType: "gaussian", component: "Ez",
      waveLength: Number((1 / 0.15).toFixed(6)), fwidth: 0.1
    })],
    flux: [monitor(cell, 1, {
      x: 1.1, y: 0, kind: "resonance", name: "Inside the ring (Harminv)", fcen: 0.15, df: 0.1
    })]
  };
};

// ---------------------------------------------------------------------------
// Feixe gaussiano focalizado atravessando uma lâmina de vidro (refração)
// ---------------------------------------------------------------------------
const gaussianBeamSlab = () => {
  const cell = { x: 16, y: 10 };
  return {
    resolution: 20,
    coordinates: { x: cell.x, y: cell.y, z: 0 },
    pml: 1,
    until: 25,
    video: true,
    geometries: [
      block(cell, {
        // Atravessa a célula inteira (inclusive o PML), como uma lâmina infinita em y
        name: "glass slab (n = 1.5)", x: 3, y: 0, width: 3, height: 10,
        n: 1.5, color: "#80DEEA", material: "glass"
      })
    ],
    sources: [source(cell, 1, {
      x: -4, y: -1.5, sourceType: "gaussian_beam", component: "Ez",
      waveLength: 1, waveWidth: 3, beamWaist: 1, beamAngle: 20, integrated: 1
    })],
    flux: []
  };
};

// ---------------------------------------------------------------------------
// Reflexão de Fresnel numa interface ar | vidro (onda plana, incidência normal).
// Bordas periódicas em y: a fonte e os monitores ocupam a altura toda, como uma onda plana
// infinita. O vidro atravessa o PML da direita (meio semi-infinito).
// ---------------------------------------------------------------------------
const fresnelInterface = () => {
  const cell = { x: 12, y: 2 };
  const fcen = 1;
  return {
    resolution: 40,
    coordinates: { x: cell.x, y: cell.y, z: 0 },
    pml: 1,
    boundary: "pml_x_periodic_y",
    until: 30,
    geometries: [
      block(cell, {
        name: "glass (n = 1.5)", x: 3, y: 0, width: 6, height: 2, n: 1.5, color: "#80DEEA",
        material: "glass"
      })
    ],
    sources: [source(cell, 1, {
      x: -4, y: 0, sourceType: "gaussian", component: "Ez",
      waveLength: 1 / fcen, fwidth: 1.2, waveSize: [0, 2], integrated: 1
    })],
    flux: [
      monitor(cell, 1, {
        x: -3, y: 0, kind: "reflection", name: "Reflection (R)",
        orientation: "vertical", length: 2, fcen, df: 0.6, nfreq: 61
      }),
      monitor(cell, 2, {
        x: 2, y: 0, kind: "transmission", name: "Transmission (T)",
        orientation: "vertical", length: 2, fcen, df: 0.6, nfreq: 61
      })
    ]
  };
};

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------
export const EXAMPLES = [
  {
    id: "pc-sensor-square",
    title: "Photonic crystal sensor — square ring",
    category: "Literature (IEEE 2025)",
    description:
      "W1 waveguide coupled to a square ring cavity in a silicon-rod photonic crystal " +
      "immersed in an analyte (n = 1.45). The resonance shifts with the analyte index, " +
      "which is how the sensor works.",
    source: "Balur, Sonkusare & Sharan, IEEE Xplore 11525706 (Fig. 5); Meep model: " +
      "github.com/electrosfi-org/meepIEEE2025Test",
    runtime: "~2–5 min",
    learn: ["Resonance (f₀, Q) with Harminv", "Transmission normalized by the crystal without cavity"],
    reference: [
      { label: "f₀ (Harminv, resolution 10)", value: "0.29434 c/µm (λ₀ ≈ 3.397 µm)" },
      { label: "f₀ (Harminv, resolution 20)", value: "0.29465 c/µm" },
      { label: "Q (Harminv)", value: "≈ 146–151" }
    ],
    build: () => photonicCrystalSensor("square")
  },
  {
    id: "pc-sensor-hexagonal",
    title: "Photonic crystal sensor — hexagonal ring",
    category: "Literature (IEEE 2025)",
    description:
      "Same crystal and waveguide with a hexagonal ring cavity, which confines light " +
      "better (higher Q) than the square ring.",
    source: "Balur, Sonkusare & Sharan, IEEE Xplore 11525706 (Fig. 3); Meep model: " +
      "github.com/electrosfi-org/meepIEEE2025Test",
    runtime: "~2–5 min",
    learn: ["Compare Q between cavity shapes"],
    reference: [
      { label: "f₀ (Harminv, resolution 10)", value: "0.29509 c/µm (λ₀ ≈ 3.389 µm)" },
      { label: "f₀ (Harminv, resolution 20)", value: "0.29544 c/µm" },
      { label: "Q (Harminv)", value: "≈ 192–193" }
    ],
    build: () => photonicCrystalSensor("hexagonal")
  },
  {
    id: "fresnel-interface",
    title: "Reflection at an air | glass interface",
    category: "Validation",
    description:
      "A plane-wave pulse hits a semi-infinite glass block (n = 1.5) at normal incidence. " +
      "The reflected and transmitted spectra must match the Fresnel equations, which makes " +
      "this the simplest check that the simulator gives correct numbers.",
    source: "Fresnel equations (any optics textbook); validated in module-2d/meep/tests",
    runtime: "< 30 s",
    learn: [
      "Reflection and transmission spectra (R, T) and the loss 1 − R − T",
      "Periodic borders for plane waves (Cell Properties → Borders)"
    ],
    reference: [
      { label: "R (analytic)", value: "((1 − n)/(1 + n))² = 0.0400 for every wavelength" },
      { label: "T (analytic)", value: "1 − R = 0.9600" },
      { label: "ElectrosFI (resolution 40)", value: "R ≈ 0.039–0.040, R + T ≈ 1" }
    ],
    steps: [
      "New 2D simulation. Cell Properties: width 12, height 2, resolution 40, time 30, PML 1, Borders = “PML in x, periodic in y”.",
      "Draw a block and set (Dimensions/Position): width 6, height 2, center (3, 0). Material: “Glass (n = 1.5)”.",
      "Add a source at (−4, 0): type Gaussian pulse, λ = 1 µm, Δf = 1.2, polarization Ez, size 0 × 2, check “Source extends into the PML”.",
      "Add two outputs (vertical, length 2, center λ = 1 µm, width Δf = 0.6, 61 points): Reflection at (−3, 0) and Transmission at (2, 0).",
      "Run Simulation and open the spectra: R ≈ 0.04, T ≈ 0.96."
    ],
    build: fresnelInterface
  },
  {
    id: "ring-resonator",
    title: "Ring resonator",
    category: "Meep tutorial",
    description:
      "A dielectric ring (n = 3.4, width 1 µm, inner radius 1 µm) excited by a short pulse. " +
      "Light circulates in the ring only at its resonant wavelengths (whispering-gallery modes).",
    source: "Meep documentation — Tutorial: Ring Resonator in Cartesian Coordinates",
    runtime: "< 1 min",
    learn: ["Several resonant modes and their Q", "Effect of resolution on the result"],
    reference: [
      { label: "f₀ (resolution 10)", value: "0.11810 · 0.14716 · 0.17525 c/µm" },
      { label: "f₀ (resolution 20)", value: "0.11818 · 0.14736 · 0.17565 c/µm" },
      { label: "Q", value: "≈ 80 · ≈ 330 · > 1000 (high Q needs longer runs)" }
    ],
    steps: [
      "New 2D simulation. Cell Properties: width 16, height 16, resolution 10, PML 2.",
      "Draw a circle of radius 2 at (0, 0), material “High-index dielectric (n = 3.4)”.",
      "Draw a second circle of radius 1 at (0, 0), material “Vacuum / Air” (drawn last, it wins).",
      "Add a source at (1.1, 0): type Gaussian pulse, λ = 6.6667 µm (f = 0.15), Δf = 0.1, polarization Ez.",
      "Add an output of type Resonance at (1.1, 0) with center λ = 6.6667 µm and Δf = 0.1.",
      "Run Simulation: the resonance table lists f₀ ≈ 0.118, 0.147 and 0.175."
    ],
    build: ringResonator
  },
  {
    id: "gaussian-beam-slab",
    title: "Gaussian beam through a glass slab",
    category: "Concepts",
    description:
      "A focused Gaussian beam (λ = 1 µm, waist 1 µm) hits a glass slab at 20°. Watch the " +
      "beam bend at each interface (Snell's law) and spread after its focus.",
    source: "ElectrosFI",
    runtime: "~1–2 min (with video)",
    learn: ["Gaussian beam source", "Refraction: sin θ₁ = n sin θ₂ (θ₂ ≈ 13.2° inside the glass)"],
    reference: [
      { label: "Angle inside the glass", value: "asin(sin 20° / 1.5) ≈ 13.2°" },
      { label: "Wavelength inside the glass", value: "λ / n ≈ 0.667 µm" }
    ],
    steps: [
      "New 2D simulation. Cell Properties: width 16, height 10, resolution 20, time 25, PML 1.",
      "Draw a block of width 3 and height 10 centered at (3, 0), material “Glass (n = 1.5)”.",
      "Add a source at (−4, −1.5): type Gaussian beam, λ = 1 µm, waist w₀ = 1, direction 20°, polarization Ez.",
      "In the plot options of Run Simulation, enable the video and run."
    ],
    build: gaussianBeamSlab
  }
];

// Documento pronto para POST /simulations (mesmo formato salvo pelo simulador 2D)
// builtinMaterials: lista de GET /materials; liga geometrias e fundo aos materiais prontos pela chave
export const buildSimulationDocument = (example, author, builtinMaterials = []) => {
  const spec = example.build();
  const byKey = new Map(builtinMaterials.filter(m => m.builtin && m.key).map(m => [m.key, m]));
  const linkMaterial = (geometry) => {
    const { materialKey, ...rest } = geometry;
    const material = byKey.get(materialKey);
    if (!material) return { ...rest, materialTitle: rest.materialTitle || "Custom" };
    return {
      ...rest,
      materialId: material._id,
      materialTitle: material.title,
      materialDescription: material.description
    };
  };
  const background = spec.background || { n: 1, color: "#ffffff", title: "Default Material" };
  const backgroundMaterial = byKey.get(background.key);
  const epsilon = background.n * background.n;
  return {
    title: example.title,
    description: `${example.description}\n\nSource: ${example.source}`,
    author,
    resolution: spec.resolution,
    coordinates: spec.coordinates,
    default_material: epsilon,
    cell: {
      fill: background.color,
      epsilon,
      material_id: backgroundMaterial ? backgroundMaterial._id : 0,
      materialTitle: backgroundMaterial ? backgroundMaterial.title : background.title,
      materialDescription: "Background material of the cell"
    },
    until: spec.until,
    pml: { thickness: spec.pml },
    boundary: spec.boundary || "pml",
    decayThreshold: spec.decayThreshold || 1e-3,
    geometries: spec.geometries.map(linkMaterial),
    sources: spec.sources,
    flux: spec.flux,
    charts: [],
    productions: [],
    scene_design: PLACEHOLDER_IMAGE,
    scene_simulation: PLACEHOLDER_IMAGE,
    plotOptions: {
      ...DEFAULT_PLOT_OPTIONS,
      generateVideo: spec.video ? 1 : 0,
      until: spec.until
    }
  };
};
